/**
 * LanguageSelect Component
 * Custom select with flag icons for language selection
 */
import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import Select, { components, SingleValue, StylesConfig } from 'react-select';

export interface LanguageOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface LanguageSelectProps {
  options: LanguageOption[];
  value: LanguageOption | null;
  onChange: (option: SingleValue<LanguageOption>) => void;
  placeholder?: string;
}

// Custom Option component with flag icon
const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <Flex align="center" gap={2}>
        <Box>{props.data.icon}</Box>
        <Text margin={0} marginBottom={0}>{props.data.label}</Text>
      </Flex>
    </components.Option>
  );
};

// Custom SingleValue component with flag icon
const CustomSingleValue = (props: any) => {
  return (
    <components.SingleValue {...props}>
      <Flex align="center" gap={2}>
        <Box>{props.data.icon}</Box>
        <Text margin={0} marginBottom={0}>{props.data.label}</Text>
      </Flex>
    </components.SingleValue>
  );
};

// Custom styles for react-select
const customStyles: StylesConfig<LanguageOption, false> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '36px',
    height: '36px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
    '&:hover': {
      borderColor: '#3182ce',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '36px',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0',
    padding: '0',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '36px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#ebf8ff'
      : state.isFocused
      ? '#f7fafc'
      : 'white',
    color: state.isSelected ? '#2c5282' : '#2d3748',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#ebf8ff',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#2d3748',
    display: 'flex',
    alignItems: 'center',
    margin: '0',
    top: '50%',
    transform: 'translateY(-50%)',
  }),
};

const LanguageSelect: React.FC<LanguageSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select language',
}) => {
  return (
    <Select<LanguageOption>
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isSearchable={false}
      isClearable={false}
      components={{
        Option: CustomOption,
        SingleValue: CustomSingleValue,
      }}
      styles={customStyles}
      className="language-select"
      classNamePrefix="language-select"
    />
  );
};

export default LanguageSelect;
